import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/helpers';
import { Save, Building, Percent, FileText, Palette, Landmark, QrCode, CreditCard, Plus, Trash2, GripVertical, Copy, Eye, Check, Lock, Star } from 'lucide-react';
import { toast } from 'sonner';
import { SectionOrderCard, ThemeStyleCard } from '../../components/settings/QuotationLayoutCards';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const qrInputRef = useRef(null);
  
  // Template Settings State
  const [templateSettings, setTemplateSettings] = useState({
    default_template_id: null,
    allow_template_choice: true,
    tenant_plan: ''
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Agency Settings
  const [settings, setSettings] = useState({
    agency_name: '',
    markup_default_pct: '',
    currency_default: '',
    quote_footnote: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    logo_url: '',
    primary_color: '#1e40af',
    secondary_color: '#3b82f6',
    login_background_color: '#0f172a',
    // Bank Details
    bank_account_name: '',
    bank_account_number: '',
    bank_name: '',
    bank_branch: '',
    bank_ifsc: '',
    upi_id: '',
    upi_qr_url: '',
    show_bank_in_quotation: true,
    show_upi_in_quotation: true,
  });

  // Payment Policies
  const [paymentPolicies, setPaymentPolicies] = useState([]);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyFormOpen, setPolicyFormOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    policy_name: '',
    is_default: false,
    instalments: [
      { instalment_order: 1, days_before_arrival: 30, amount_type: 'percentage', amount_value: 30 },
      { instalment_order: 2, days_before_arrival: 7, amount_type: 'balance', amount_value: null }
    ]
  });

  useEffect(() => {
    fetchSettings();
    fetchPaymentPolicies();
    fetchTemplateSettings();
  }, []);

  const fetchTemplateSettings = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.get('/templates/tenant/available');
      setAvailableTemplates(response.data.templates || []);
      setTemplateSettings({
        default_template_id: response.data.default_template_id,
        allow_template_choice: response.data.allow_template_choice ?? true,
        tenant_plan: response.data.tenant_plan || 'No plan'
      });
    } catch (error) {
      console.error('Failed to load template settings:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSettingsSave = async () => {
    try {
      await api.put('/templates/tenant/settings', {
        default_template_id: templateSettings.default_template_id,
        allow_template_choice: templateSettings.allow_template_choice
      });
      toast.success('Template settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template settings');
    }
  };

  const handleSetDefaultTemplate = async (templateId) => {
    const template = availableTemplates.find(t => t.template_id === templateId);
    if (!template?.is_available) {
      toast.error(`This template requires a higher subscription plan`);
      return;
    }
    
    setTemplateSettings(prev => ({ ...prev, default_template_id: templateId }));
    try {
      await api.put('/templates/tenant/settings', {
        default_template_id: templateId,
        allow_template_choice: templateSettings.allow_template_choice
      });
      toast.success('Default template updated');
      
      // Update local state
      setAvailableTemplates(prev => prev.map(t => ({
        ...t,
        is_tenant_default: t.template_id === templateId
      })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default template');
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    try {
      const response = await api.get(`/templates/tenant/preview/${templateId}`);
      setPreviewTemplate(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to load template preview');
    }
  };

  const fetchPaymentPolicies = async () => {
    try {
      const response = await api.get('/payment-policies');
      setPaymentPolicies(response.data || []);
    } catch (error) {
      console.error('Failed to load payment policies:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data) {
        setSettings({
          agency_name: response.data.agency_name || '',
          markup_default_pct: response.data.markup_default_pct?.toString() || '20',
          currency_default: response.data.currency_default || 'INR',
          quote_footnote: response.data.quote_footnote || '',
          contact_person: response.data.contact_person || '',
          contact_email: response.data.contact_email || '',
          contact_phone: response.data.contact_phone || '',
          address: response.data.address || '',
          website: response.data.website || '',
          logo_url: response.data.logo_url || '',
          primary_color: response.data.primary_color || '#1e40af',
          secondary_color: response.data.secondary_color || '#3b82f6',
          login_background_color: response.data.login_background_color || '#0f172a',
          // Bank Details
          bank_account_name: response.data.bank_account_name || '',
          bank_account_number: response.data.bank_account_number || '',
          bank_name: response.data.bank_name || '',
          bank_branch: response.data.bank_branch || '',
          bank_ifsc: response.data.bank_ifsc || '',
          upi_id: response.data.upi_id || '',
          upi_qr_url: response.data.upi_qr_url || '',
          show_bank_in_quotation: response.data.show_bank_in_quotation ?? true,
          show_upi_in_quotation: response.data.show_upi_in_quotation ?? true,
          quotation_layout: response.data.quotation_layout || null,
          quotation_section_catalog: response.data.quotation_section_catalog || null,
        });
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        agency_name: settings.agency_name,
        markup_default_pct: parseFloat(settings.markup_default_pct),
        currency_default: settings.currency_default,
        quote_footnote: settings.quote_footnote,
        contact_person: settings.contact_person,
        contact_email: settings.contact_email,
        contact_phone: settings.contact_phone,
        address: settings.address,
        website: settings.website,
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        login_background_color: settings.login_background_color,
        // Bank Details
        bank_account_name: settings.bank_account_name,
        bank_account_number: settings.bank_account_number,
        bank_name: settings.bank_name,
        bank_branch: settings.bank_branch,
        bank_ifsc: settings.bank_ifsc,
        upi_id: settings.upi_id,
        upi_qr_url: settings.upi_qr_url,
        show_bank_in_quotation: settings.show_bank_in_quotation,
        show_upi_in_quotation: settings.show_upi_in_quotation,
        ...(settings.quotation_layout ? { quotation_layout: settings.quotation_layout } : {}),
      };
      
      await api.put('/admin/settings', data);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/agency-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  // Handle UPI QR Code upload (Base64)
  const handleQrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 500KB for QR)
    if (file.size > 500 * 1024) {
      toast.error('QR image must be less than 500KB');
      return;
    }

    // Upload the image and keep only its URL (a base64 image is too long for the saved field).
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload/agency-upi-qr', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings((prev) => ({ ...prev, upi_qr_url: response.data.upi_qr_url }));
      toast.success('QR code uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload QR image');
    }
  };

  // Payment Policy handlers
  const handlePolicySubmit = async () => {
    try {
      if (editingPolicy) {
        await api.put(`/payment-policies/${editingPolicy}`, policyForm);
        toast.success('Payment policy updated');
      } else {
        await api.post('/payment-policies', policyForm);
        toast.success('Payment policy created');
      }
      fetchPaymentPolicies();
      resetPolicyForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment policy');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Delete this payment policy?')) return;
    try {
      await api.delete(`/payment-policies/${policyId}`);
      toast.success('Payment policy deleted');
      fetchPaymentPolicies();
    } catch (error) {
      toast.error('Failed to delete payment policy');
    }
  };

  const resetPolicyForm = () => {
    setPolicyFormOpen(false);
    setEditingPolicy(null);
    setPolicyForm({
      policy_name: '',
      is_default: false,
      instalments: [
        { instalment_order: 1, days_before_arrival: 30, amount_type: 'percentage', amount_value: 30 },
        { instalment_order: 2, days_before_arrival: 7, amount_type: 'balance', amount_value: null }
      ]
    });
  };

  const editPolicy = (policy) => {
    setEditingPolicy(policy.policy_id);
    setPolicyForm({
      policy_name: policy.policy_name,
      is_default: policy.is_default,
      instalments: policy.instalments.map(i => ({
        instalment_order: i.instalment_order,
        days_before_arrival: i.days_before_arrival,
        amount_type: i.amount_type,
        amount_value: i.amount_value
      }))
    });
    setPolicyFormOpen(true);
  };

  const addInstalment = () => {
    const nextOrder = policyForm.instalments.length + 1;
    setPolicyForm({
      ...policyForm,
      instalments: [
        ...policyForm.instalments,
        { instalment_order: nextOrder, days_before_arrival: 0, amount_type: 'balance', amount_value: null }
      ]
    });
  };

  const removeInstalment = (index) => {
    const updated = policyForm.instalments.filter((_, i) => i !== index).map((inst, i) => ({
      ...inst,
      instalment_order: i + 1
    }));
    setPolicyForm({ ...policyForm, instalments: updated });
  };

  const updateInstalment = (index, field, value) => {
    const updated = [...policyForm.instalments];
    updated[index] = { ...updated[index], [field]: value };
    setPolicyForm({ ...policyForm, instalments: updated });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading settings...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Agency Settings</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Configure your agency preferences</p>
        </div>

        <form onSubmit={handleSettingsSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Agency Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {settings.logo_url ? (
                    <img src={getImageUrl(settings.logo_url)} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <Label>Agency Logo</Label>
                  <Input type="file" accept="image/*" onChange={handleLogoUpload} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Agency Name</Label>
                  <Input
                    value={settings.agency_name}
                    onChange={(e) => setSettings({ ...settings, agency_name: e.target.value })}
                    placeholder="e.g., Cherry Holidays"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={settings.website}
                    onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                    placeholder="e.g., www.cherryholidays.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input
                    value={settings.contact_person}
                    onChange={(e) => setSettings({ ...settings, contact_person: e.target.value })}
                    placeholder="Contact name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    placeholder="email@agency.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={settings.contact_phone}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  placeholder="Full address"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Agency Branding Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Login Page Branding
              </CardTitle>
              <p className="text-sm text-gray-500">Customize your login page appearance</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.primary_color || '#1e40af'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color || '#1e40af'}
                      onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                      placeholder="#1e40af"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.secondary_color || '#3b82f6'}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_color || '#3b82f6'}
                      onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Login Background</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.login_background_color || '#0f172a'}
                      onChange={(e) => setSettings({ ...settings, login_background_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={settings.login_background_color || '#0f172a'}
                      onChange={(e) => setSettings({ ...settings, login_background_color: e.target.value })}
                      placeholder="#0f172a"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <div 
                  className="h-20 rounded-lg flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(to bottom right, ${settings.login_background_color || '#0f172a'}, ${settings.primary_color || '#1e40af'}20)` 
                  }}
                >
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ 
                      background: `linear-gradient(to right, ${settings.primary_color || '#1e40af'}, ${settings.secondary_color || '#3b82f6'})` 
                    }}
                  >
                    Login Button Preview
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Pricing Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Markup Percentage</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={settings.markup_default_pct}
                      onChange={(e) => setSettings({ ...settings, markup_default_pct: e.target.value })}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <Input
                    value={settings.currency_default}
                    onChange={(e) => setSettings({ ...settings, currency_default: e.target.value })}
                    placeholder="e.g., INR"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Quote Footnote
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings.quote_footnote}
                onChange={(e) => setSettings({ ...settings, quote_footnote: e.target.value })}
                placeholder="Terms and conditions for quotations..."
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Bank Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="w-5 h-5" />
                Bank Details
              </CardTitle>
              <p className="text-sm text-gray-500">Bank details shown on quotation for payment</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="show-bank">Show Bank Details in Quotation</Label>
                <Switch
                  id="show-bank"
                  checked={settings.show_bank_in_quotation}
                  onCheckedChange={(checked) => setSettings({ ...settings, show_bank_in_quotation: checked })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Holder Name</Label>
                  <Input
                    value={settings.bank_account_name}
                    onChange={(e) => setSettings({ ...settings, bank_account_name: e.target.value })}
                    placeholder="e.g., Cherry Holidays Pvt Ltd"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={settings.bank_account_number}
                    onChange={(e) => setSettings({ ...settings, bank_account_number: e.target.value })}
                    placeholder="e.g., 1234567890123"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={settings.bank_name}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="e.g., HDFC Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Input
                    value={settings.bank_branch}
                    onChange={(e) => setSettings({ ...settings, bank_branch: e.target.value })}
                    placeholder="e.g., Koramangala, Bangalore"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={settings.bank_ifsc}
                    onChange={(e) => setSettings({ ...settings, bank_ifsc: e.target.value.toUpperCase() })}
                    placeholder="e.g., HDFC0001234"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* UPI Details Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                UPI Payment Details
              </CardTitle>
              <p className="text-sm text-gray-500">UPI ID and QR code shown on quotation</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="show-upi">Show UPI in Quotation</Label>
                <Switch
                  id="show-upi"
                  checked={settings.show_upi_in_quotation}
                  onCheckedChange={(checked) => setSettings({ ...settings, show_upi_in_quotation: checked })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>UPI ID</Label>
                  <Input
                    value={settings.upi_id}
                    onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })}
                    placeholder="e.g., yourname@upi"
                    data-testid="upi-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UPI QR Code</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                      {settings.upi_qr_url ? (
                        <img src={settings.upi_qr_url} data-testid="upi-qr-preview" alt="UPI QR" className="w-full h-full object-contain" />
                      ) : (
                        <QrCode className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <Input 
                        ref={qrInputRef}
                        type="file" 
                        accept="image/png,image/jpeg,image/jpg" 
                        onChange={handleQrUpload} data-testid="upi-qr-file-input"
                        className="hidden"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => qrInputRef.current?.click()}
                      >
                        Upload QR Code
                      </Button>
                      {settings.upi_qr_url && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="ml-2 text-red-600"
                          onClick={() => setSettings({ ...settings, upi_qr_url: '' })}
                          data-testid="upi-qr-remove-btn"
                        >
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Max 500KB, PNG or JPEG</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {settings.quotation_layout && settings.quotation_section_catalog && (
            <>
              <SectionOrderCard catalog={settings.quotation_section_catalog} layout={settings.quotation_layout}
                onChange={(l) => setSettings((prev) => ({ ...prev, quotation_layout: l }))} />
              <ThemeStyleCard catalog={settings.quotation_section_catalog} layout={settings.quotation_layout}
                onChange={(l) => setSettings((prev) => ({ ...prev, quotation_layout: l }))} />
            </>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>

        {/* Payment Policies Section */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Policies
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">Define instalment schedules for quotations</p>
              </div>
              <Button 
                onClick={() => { resetPolicyForm(); setPolicyFormOpen(true); }}
                size="sm"
                data-testid="add-payment-policy-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Policy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Policy List */}
            {paymentPolicies.length === 0 && !policyFormOpen && (
              <p className="text-gray-500 text-sm">No payment policies defined. Create one to get started.</p>
            )}
            
            {paymentPolicies.length > 0 && !policyFormOpen && (
              <div className="space-y-3">
                {paymentPolicies.map(policy => (
                  <div 
                    key={policy.policy_id} 
                    className="border rounded-lg p-4 hover:bg-gray-50"
                    data-testid={`payment-policy-${policy.policy_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {policy.policy_name}
                          {policy.is_default && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Default</span>
                          )}
                        </h4>
                        <div className="text-sm text-gray-600 mt-1">
                          {policy.instalments?.map((inst, idx) => (
                            <span key={idx}>
                              {idx > 0 && ' + '}
                              {inst.amount_type === 'percentage' ? `${inst.amount_value}%` : 
                               inst.amount_type === 'fixed' ? `₹${inst.amount_value}` : 'Balance'}
                              {' '}({inst.days_before_arrival} days before)
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => editPolicy(policy)}>
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600"
                          onClick={() => handleDeletePolicy(policy.policy_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Policy Form */}
            {policyFormOpen && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-4" data-testid="payment-policy-form">
                <div className="space-y-2">
                  <Label>Policy Name</Label>
                  <Input
                    value={policyForm.policy_name}
                    onChange={(e) => setPolicyForm({ ...policyForm, policy_name: e.target.value })}
                    placeholder="e.g., Standard 30-70 Policy"
                    data-testid="policy-name-input"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    id="policy-default"
                    checked={policyForm.is_default}
                    onCheckedChange={(checked) => setPolicyForm({ ...policyForm, is_default: checked })}
                  />
                  <Label htmlFor="policy-default">Set as Default Policy</Label>
                </div>

                <div className="space-y-2">
                  <Label>Instalments</Label>
                  <div className="space-y-2">
                    {policyForm.instalments.map((inst, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                        <select
                          value={inst.amount_type}
                          onChange={(e) => updateInstalment(index, 'amount_type', e.target.value)}
                          className="border rounded px-2 py-1 text-sm"
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                          <option value="balance">Balance</option>
                        </select>
                        {inst.amount_type !== 'balance' && (
                          <Input
                            type="number"
                            value={inst.amount_value || ''}
                            onChange={(e) => updateInstalment(index, 'amount_value', parseFloat(e.target.value) || null)}
                            placeholder={inst.amount_type === 'percentage' ? '%' : '₹'}
                            className="w-20"
                          />
                        )}
                        <span className="text-sm text-gray-500">due</span>
                        <Input
                          type="number"
                          value={inst.days_before_arrival}
                          onChange={(e) => updateInstalment(index, 'days_before_arrival', parseInt(e.target.value) || 0)}
                          className="w-16"
                        />
                        <span className="text-sm text-gray-500">days before arrival</span>
                        {policyForm.instalments.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeInstalment(index)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addInstalment}>
                    <Plus className="w-4 h-4 mr-1" /> Add Instalment
                  </Button>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={resetPolicyForm}>Cancel</Button>
                  <Button onClick={handlePolicySubmit} data-testid="save-policy-btn">
                    {editingPolicy ? 'Update Policy' : 'Create Policy'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quotation Templates Settings */}
        <Card data-testid="template-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Quotation Templates
            </CardTitle>
            <CardDescription>
              Choose your default template and control template access for your team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template Choice Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow Users to Choose Templates</Label>
                <p className="text-xs text-gray-500 mt-1">
                  When disabled, all quotations will use your default template
                </p>
              </div>
              <Switch
                checked={templateSettings.allow_template_choice}
                onCheckedChange={(checked) => {
                  setTemplateSettings(prev => ({ ...prev, allow_template_choice: checked }));
                  api.put('/templates/tenant/settings', {
                    default_template_id: templateSettings.default_template_id,
                    allow_template_choice: checked
                  }).then(() => {
                    toast.success(checked ? 'Users can now choose templates' : 'All quotations will use default template');
                  }).catch(() => {
                    toast.error('Failed to update setting');
                  });
                }}
                data-testid="allow-template-choice-toggle"
              />
            </div>

            {/* Current Plan Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Your Plan:</span>
              <Badge variant="secondary" className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white">
                {templateSettings.tenant_plan}
              </Badge>
            </div>

            {/* Template Grid */}
            {loadingTemplates ? (
              <div className="text-center py-8 text-gray-500">Loading templates...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableTemplates.map((template) => (
                  <div
                    key={template.template_id}
                    className={`relative border rounded-lg overflow-hidden transition-all ${
                      template.is_tenant_default 
                        ? 'border-cyan-500 ring-2 ring-cyan-500/20' 
                        : template.is_available 
                          ? 'border-gray-200 hover:border-gray-300' 
                          : 'border-gray-200 opacity-60'
                    }`}
                    data-testid={`template-card-${template.template_slug}`}
                  >
                    {/* Template Thumbnail/Preview */}
                    <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      {template.thumbnail_url ? (
                        <img 
                          src={template.thumbnail_url} 
                          alt={template.template_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileText className="w-12 h-12 text-gray-300" />
                      )}
                      
                      {/* Badges */}
                      <div className="absolute top-2 left-2 flex gap-1">
                        {template.is_tenant_default && (
                          <Badge className="bg-cyan-500 text-white text-xs">
                            <Star className="w-3 h-3 mr-1" /> Default
                          </Badge>
                        )}
                        {template.is_default && !template.is_tenant_default && (
                          <Badge variant="secondary" className="text-xs">System Default</Badge>
                        )}
                      </div>
                      
                      {!template.is_available && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Badge variant="destructive" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            {template.allowed_plans?.includes('All plans')
                              ? 'Active subscription required'
                              : `${template.allowed_plans.join(' / ')} plan required`}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Template Info */}
                    <div className="p-3">
                      <h4 className="font-medium text-sm text-gray-900">{template.template_name}</h4>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {template.description || 'No description'}
                      </p>
                      
                      {/* Plan Badges */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.allowed_plans?.map(plan => (
                          <Badge 
                            key={plan} 
                            variant="outline" 
                            className={`text-xs ${
                              plan === templateSettings.tenant_plan 
                                ? 'border-green-500 text-green-600' 
                                : 'border-gray-300 text-gray-500'
                            }`}
                          >
                            {plan}
                          </Badge>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => handlePreviewTemplate(template.template_id)}
                          data-testid={`preview-template-${template.template_slug}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                        {template.is_available && !template.is_tenant_default && (
                          <Button
                            size="sm"
                            className="flex-1 text-xs bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                            onClick={() => handleSetDefaultTemplate(template.template_id)}
                            data-testid={`set-default-${template.template_slug}`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Set Default
                          </Button>
                        )}
                        {template.is_tenant_default && (
                          <Badge className="flex-1 justify-center bg-cyan-100 text-cyan-700 text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Current Default
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Template Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {previewTemplate?.template_name} - Preview
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto border rounded-lg bg-white">
              {previewTemplate && (
                <div 
                  className="p-4"
                  dangerouslySetInnerHTML={{ 
                    __html: `<style>${previewTemplate.css_content || ''}</style>${previewTemplate.html_content || '<p>No preview available</p>'}` 
                  }}
                />
              )}
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-gray-500">
                Available for: {previewTemplate?.allowed_plans?.join(', ')}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                  Close
                </Button>
                {availableTemplates.find(t => t.template_id === previewTemplate?.template_id)?.is_available && (
                  <Button 
                    className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                    onClick={() => {
                      handleSetDefaultTemplate(previewTemplate.template_id);
                      setShowPreviewDialog(false);
                    }}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Set as Default
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Settings;
